/**
 * Error Pattern Detection Workflow
 *
 * This workflow detects recurring error patterns from client logs:
 * 1. Triggers when a new client log is created
 * 2. Normalizes the log message
 * 3. Uses AI to match against known error patterns
 * 4. Escalates if pattern is recurring or critical
 * 5. Notifies engineering team of patterns
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const errorPatternDetectionWorkflow: Workflow = {
  id: 'error-pattern-detection',
  name: 'Rilevamento Pattern Errori',
  description: 'Analizza i log client per identificare pattern ricorrenti e problemi critici',
  trigger: {
    type: 'event',
    config: {
      eventType: 'entity.created',
      entityType: 'client_log',
      // Only process error and warning logs
      filterField: 'level',
      filterValues: ['error', 'warning'],
    },
  },
  steps: [
    {
      id: 'normalize_log',
      name: 'Normalizza Log',
      type: 'custom',
      config: {
        // Normalize dynamic parts (IDs, timestamps, etc.)
        removeDynamicValues: true,
        extractStackTrace: true,
        extractErrorCode: true,
        // Create fingerprint for deduplication
        generateFingerprint: true,
      },
      next: ['match_pattern'],
    },
    {
      id: 'match_pattern',
      name: 'Confronta Pattern',
      type: 'ai_analyze',
      config: {
        prompt: `Analyze this error log and match it against known patterns.

Current error:
Message: {message}
Stack trace: {stackTrace}
Error code: {errorCode}
Context: {context}

Recent similar errors:
{recentErrors}

Determine:
1. Is this a new error pattern or recurring?
2. Severity level (critical/high/medium/low)
3. Potential root cause
4. Is this affecting multiple users?
5. Suggested resolution if known

Respond in JSON:
{
  "pattern": {
    "isRecurring": true/false,
    "occurrenceCount": 0,
    "firstSeen": "timestamp or null",
    "affectedUsers": 0
  },
  "severity": "critical|high|medium|low",
  "rootCause": "Suspected cause",
  "suggestedFix": "Known fix or investigation steps",
  "requiresEscalation": true/false
}`,
      },
      next: ['escalate_if_critical'],
    },
    {
      id: 'escalate_if_critical',
      name: 'Escalation se Critico',
      type: 'custom',
      config: {
        // Escalate if critical or recurring
        escalateIfCritical: true,
        escalateIfRecurring: true,
        recurringThreshold: 5, // occurrences
        createIssue: true,
        assignToTeam: 'engineering',
      },
      condition: {
        field: 'requiresEscalation',
        operator: '==',
        value: true,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Notifica Team',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: 'Pattern errore rilevato: {errorCode} | Occorrenze: {count} | Severità: {severity}',
        notifyEngineering: true,
        includeStackTrace: true,
        includeSuggestedFix: true,
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate error fingerprint for deduplication
 */
export function generateErrorFingerprint(message: string, stackTrace?: string): string {
  // Remove dynamic parts (IDs, numbers, timestamps)
  const normalized = message
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, 'UUID')
    .replace(/\b\d+\b/g, 'N')
    .replace(/\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/g, 'TIMESTAMP');

  // Use first line of stack trace if available
  const stackLine = stackTrace?.split('\n')[0] || '';

  // Simple hash function
  const str = `${normalized}|${stackLine}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return hash.toString(16);
}
